/**
 * Adaptive Tenant Branding Hook
 *
 * Queries TenantBranding from GraphQL API (DynamoDB backend).
 * All data comes from the backend. No mock mode.
 *
 * FERP Principles:
 * - Data-driven: Terminology comes from backend
 * - Dumb frontend: UI adapts to whatever backend returns
 * - Zero hardcoding
 *
 * Usage:
 * ```tsx
 * const { translateTerm, branding, loading } = useAdaptiveTenantBranding(tenantId);
 *
 * // Industry-specific label
 * <Text>{translateTerm('workflow')}</Text>
 * // Healthcare: "Clinical Pathway"
 * // Financial: "Transaction Flow"
 * // Legal: "Matter"
 * ```
 */

import { useTenantBranding, type UseTenantBrandingResult } from './useTenantBranding';

export interface AdaptiveTenantBrandingResult extends UseTenantBrandingResult {
  /** Whether using mock data (always false) */
  isMockMode: false;
}

/**
 * Tenant branding hook — queries backend for all branding/terminology data.
 *
 * @param tenantId - Tenant ID (from auth context)
 */
export function useAdaptiveTenantBranding(tenantId: string | null): AdaptiveTenantBrandingResult {
  const result = useTenantBranding(tenantId);

  return {
    ...result,
    isMockMode: false,
  };
}

export default useAdaptiveTenantBranding;
